/**
 * Composant `EmptyState` réutilisable — UX backlog 11.3.
 *
 * Pourquoi
 * --------
 * Les onglets vides (Watch, Sources, Channels, Posts) affichaient juste un
 * texte gris "Aucun X". Sans illustration ni CTA, l'utilisateur ne savait pas
 * quoi faire ensuite. Ce composant standardise un empty state engageant :
 *   - Une grosse icône dans un cercle violet pâle
 *   - Un titre + une description
 *   - Un CTA primaire (action principale) + optionnel CTA secondaire
 *   - Animation fade + slide-up à l'apparition (framer-motion)
 *
 * Usage
 * -----
 * ```tsx
 * <EmptyState
 *   icon={Eye}
 *   title="Aucun centre d'intérêt"
 *   description="Commencez par décrire les sujets à surveiller"
 *   primaryCta={{ label: 'Ajouter', icon: Plus, onClick: handleAdd }}
 *   secondaryCta={{ label: 'Suggérer via IA', onClick: handleSuggest }}
 * />
 * ```
 */
import { LucideIcon, Plus, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { AnimatedIconButton } from './AnimatedIconButton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

interface EmptyStateCta {
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  disabled?: boolean;
  /** Backlog V3-2.2 — tooltip Radix (rendu au survol même si le bouton est disabled). */
  disabledTooltip?: string;
}

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  primaryCta?: EmptyStateCta;
  secondaryCta?: EmptyStateCta;
  /** Marge verticale du conteneur (par défaut py-12). */
  paddingY?: string;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  primaryCta,
  secondaryCta,
  paddingY = 'py-12',
}: EmptyStateProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={`flex flex-col items-center justify-center text-center px-6 ${paddingY}`}
    >
      <div
        className="flex items-center justify-center rounded-full mb-4"
        style={{
          width: 64,
          height: 64,
          backgroundColor: 'rgba(85, 0, 255, 0.1)',
        }}
      >
        <Icon className="w-8 h-8" style={{ color: 'var(--color-primary)' }} />
      </div>
      <h3
        className="text-lg font-semibold mb-1"
        style={{ color: 'var(--color-text-primary)' }}
      >
        {title}
      </h3>
      <p
        className="text-sm max-w-md mb-5"
        style={{ color: 'var(--color-text-secondary)' }}
      >
        {description}
      </p>
      {/* Cohérence visuelle (19-05) : on utilise EXACTEMENT le même
          composant que dans les headers (`AnimatedIconButton` / `ActionButtonBase`)
          pour que les deux CTAs ne soient pas dépareillés selon l'endroit où
          ils apparaissent (header tab vs empty state).
          Le primary garde le variant "default" (violet plein au hover).
          Le secondary utilise aussi "default" → cohérence avec le bouton
          "Suggérer via IA" dans le header. Le `expanded` est forcé pour que le
          label soit toujours visible (sans hover-only). */}
      {(primaryCta || secondaryCta) && (
        <TooltipProvider delayDuration={150}>
          <div className="flex flex-wrap items-center gap-2 justify-center">
            {/* 1.3 — le CTA primaire (souvent « Suggérer via IA ») supporte
                désormais un disabledTooltip, comme le secondaire : un état
                disabled doit toujours être expliqué, quel que soit le rang. */}
            {primaryCta && (
              primaryCta.disabled && primaryCta.disabledTooltip ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">
                      <AnimatedIconButton
                        icon={primaryCta.icon ?? Sparkles}
                        label={primaryCta.label}
                        onClick={primaryCta.onClick}
                        variant="default"
                        disabled={true}
                        expanded
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs text-center">
                    {primaryCta.disabledTooltip}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <AnimatedIconButton
                  icon={primaryCta.icon ?? Sparkles}
                  label={primaryCta.label}
                  onClick={primaryCta.onClick}
                  variant="default"
                  disabled={primaryCta.disabled}
                  expanded
                />
              )
            )}
            {/* Secondaire en variant neutral pour la hiérarchie visuelle (1.3). */}
            {secondaryCta && (
              secondaryCta.disabled && secondaryCta.disabledTooltip ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* Span wrapper nécessaire : un bouton disabled ne fire pas les
                        events DOM, donc Radix Tooltip ne reçoit pas le hover. Le
                        span ne consomme ni le click ni le focus, juste les hovers. */}
                    <span className="inline-flex">
                      <AnimatedIconButton
                        icon={secondaryCta.icon ?? Plus}
                        label={secondaryCta.label}
                        onClick={secondaryCta.onClick}
                        variant="neutral"
                        disabled={true}
                        expanded
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs text-center">
                    {secondaryCta.disabledTooltip}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <AnimatedIconButton
                  icon={secondaryCta.icon ?? Plus}
                  label={secondaryCta.label}
                  onClick={secondaryCta.onClick}
                  variant="neutral"
                  disabled={secondaryCta.disabled}
                  expanded
                />
              )
            )}
          </div>
        </TooltipProvider>
      )}
    </motion.div>
  );
}
